"use client";

import { useState, type ReactNode } from "react";
import { Minus, Plus } from "lucide-react";

import { cn } from "@/lib/utils";

import styles from "./pdp-layout.module.css";
import { StickyBar } from "./sticky-bar";

interface TotalStepperAtcCardProps {
	totalLabel: string;
	unitPriceLabel?: string | null;
	totalLabelsByQty: Record<number, string>;
	quantityLabelsByQty?: Record<number, string>;
	savingsLabelsByQty?: Record<number, string | null>;
	initialQty?: number;
	min?: number;
	max?: number;
	decreaseAriaLabel: string;
	increaseAriaLabel: string;
	inputName?: string;
	atcButton: ReactNode;
	stickyBar?: {
		productName: string;
		expressBadge?: ReactNode;
		show: boolean;
	};
}

export function TotalStepperAtcCard({
	totalLabel,
	unitPriceLabel,
	totalLabelsByQty,
	quantityLabelsByQty,
	savingsLabelsByQty,
	initialQty = 1,
	min = 1,
	max = 10,
	decreaseAriaLabel,
	increaseAriaLabel,
	inputName = "quantity",
	atcButton,
	stickyBar,
}: TotalStepperAtcCardProps) {
	const [qty, setQty] = useState(initialQty);
	const total = totalLabelsByQty[qty] ?? totalLabelsByQty[min] ?? "";
	const quantityLabel = quantityLabelsByQty?.[qty] ?? quantityLabelsByQty?.[min] ?? null;
	const savings = savingsLabelsByQty?.[qty] ?? null;

	const stepperBtn =
		"flex min-h-11 min-w-11 items-center justify-center text-foreground transition-colors hover:bg-secondary hover:text-primary disabled:cursor-not-allowed disabled:opacity-40";

	return (
		<div className="bg-secondary/40 mt-2 flex flex-col gap-3 rounded-2xl border border-border p-3.5 sm:p-4">
			<input type="hidden" name={inputName} value={qty} />
			{/* Price-Block oben */}
			<div className="flex flex-col">
				<span className="font-mono text-[0.58rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
					{totalLabel}
				</span>
				<span className="mt-1 font-display text-[1.45rem] font-medium tabular-nums leading-none tracking-[-0.018em] text-primary-hero sm:text-[1.65rem]">
					{total}
				</span>
				{quantityLabel ? <span className="mt-1 text-xs text-muted-foreground">{quantityLabel}</span> : null}
				{unitPriceLabel ? <span className="mt-1 text-xs text-muted-foreground">{unitPriceLabel}</span> : null}
				{savings && (
					<span className="mt-1.5 font-mono text-[0.62rem] font-semibold uppercase tracking-[0.14em] text-success">
						{savings}
					</span>
				)}
			</div>
			{/* Qty + ATC in einer Reihe drunter */}
			<div className={styles.purchaseActions}>
				<div className="flex h-12 shrink-0 items-center rounded-full border border-border bg-card sm:h-14">
					<button
						type="button"
						aria-label={decreaseAriaLabel}
						onClick={() => setQty((q) => Math.max(min, q - 1))}
						disabled={qty <= min}
						className={cn(stepperBtn, "rounded-l-full")}
					>
						<Minus className="h-4 w-4" />
					</button>
					<span
						aria-live="polite"
						className="w-10 text-center font-mono text-base font-semibold tabular-nums"
					>
						{qty}
					</span>
					<button
						type="button"
						aria-label={increaseAriaLabel}
						onClick={() => setQty((q) => Math.min(max, q + 1))}
						disabled={qty >= max}
						className={cn(stepperBtn, "rounded-r-full")}
					>
						<Plus className="h-4 w-4" />
					</button>
				</div>
				<div className="flex-1">{atcButton}</div>
			</div>
			{stickyBar ? (
				<StickyBar
					productName={stickyBar.productName}
					expressBadge={stickyBar.expressBadge}
					show={stickyBar.show}
					priceContent={
						<div>
							<div>{total}</div>
							{quantityLabel ? (
								<div className="text-[0.72rem] text-muted-foreground">{quantityLabel}</div>
							) : null}
						</div>
					}
				/>
			) : null}
		</div>
	);
}
